import { Link } from 'react-router-dom';

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-bg-primary py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/login">
            <img
              src="/my-family-calendar-primary-icon.png?v=2"
              alt="My Family Calendar"
              className="w-16 h-16 mx-auto mb-4 rounded-xl"
            />
          </Link>
          <h1 className="text-3xl font-semibold text-text-primary">Privacy Policy</h1>
          <p className="text-text-secondary mt-2">Last updated: January 2026</p>
        </div>

        {/* Content */}
        <div className="card prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Overview</h2>
            <p className="text-text-secondary mb-4">
              My Family Calendar ("we", "our", or "the app") is a family scheduling application
              that helps households coordinate calendars, tasks, and lists. This privacy policy
              explains how we collect, use, and protect your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Data We Access from Google</h2>
            <p className="text-text-secondary mb-4">
              When you connect your Google account, we request access to the following:
            </p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>
                <strong>Google Calendar (read-only)</strong>: We read your calendar events to display
                them on your family dashboard. We do not create, modify, or delete your calendar events.
              </li>
              <li>
                <strong>Google Tasks</strong>: We read and write task lists to sync with your family
                lists. This enables bidirectional synchronization between the app and Google Tasks.
              </li>
              <li>
                <strong>Basic profile information</strong>: We access your email address and name to
                identify your account within your family group.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Data We Access from Microsoft</h2>
            <p className="text-text-secondary mb-4">
              When you connect your Microsoft account, we request access to:
            </p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>
                <strong>Outlook Calendar (read-only)</strong>: We read your calendar events to display
                them on your family dashboard. We do not create, modify, or delete your calendar events.
              </li>
              <li>
                <strong>Basic profile information</strong>: We access your email address and name to
                identify your account within your family group.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">How We Use Your Data</h2>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>Calendar events are displayed on your family dashboard so all family members can see scheduled activities</li>
              <li>Tasks are synced bidirectionally with Google Tasks to keep your lists in sync</li>
              <li>We use your email to identify you within your family group</li>
              <li>We do not sell, rent, or share your personal data with third parties</li>
              <li>We do not use your data for advertising purposes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Google API Services User Data Policy Compliance</h2>
            <p className="text-text-secondary mb-4">
              My Family Calendar's use of information received from Google APIs adheres to the{' '}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue hover:underline"
              >
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements.
            </p>
            <p className="text-text-secondary mb-4">
              We only use Google user data to provide and improve the app's user-facing features:
            </p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
              <li>Displaying your calendar events on the family dashboard</li>
              <li>Syncing tasks bidirectionally with Google Tasks</li>
            </ul>
            <p className="text-text-secondary mb-4">
              We do not use Google user data for:
            </p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>Advertising or marketing purposes</li>
              <li>Selling or transferring to third parties (except as necessary to provide app features)</li>
              <li>Training AI/ML models unrelated to app functionality</li>
              <li>Any purpose other than providing the app's core features</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Data Storage and Security</h2>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>OAuth access tokens are encrypted using AES-256 encryption before being stored</li>
              <li>Calendar data is cached temporarily to improve performance and reduce API calls</li>
              <li>All data is transmitted over HTTPS encrypted connections</li>
              <li>We do not store the content of your calendar events long-term; data is fetched on-demand from Google/Microsoft</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Data Retention</h2>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>When you disconnect a calendar source, we delete the associated OAuth tokens immediately</li>
              <li>Cached calendar data is cleared within 24 hours of disconnection</li>
              <li>You can request complete deletion of your account and all associated data at any time</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Your Rights</h2>
            <p className="text-text-secondary mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>Disconnect your Google or Microsoft account at any time from Settings</li>
              <li>Request a copy of your data</li>
              <li>Request deletion of your account and all associated data</li>
              <li>Revoke access from your Google or Microsoft account settings</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Third-Party Services</h2>
            <p className="text-text-secondary mb-4">
              This app integrates with the following third-party services:
            </p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>Google Calendar API - for reading calendar events</li>
              <li>Google Tasks API - for syncing task lists</li>
              <li>Microsoft Graph API - for reading Outlook calendar events</li>
              <li>Google Maps API - for calculating travel times (optional feature)</li>
            </ul>
            <p className="text-text-secondary mt-4">
              Each of these services has their own privacy policy governing how they handle your data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Self-Hosted Instances</h2>
            <p className="text-text-secondary mb-4">
              My Family Calendar is open-source software that can be self-hosted. If you are using
              a self-hosted instance of this application:
            </p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>
                The hosting party (organization or individual running the server) is responsible
                for their own data handling practices and privacy policy.
              </li>
              <li>
                This privacy policy applies only to the official hosted instance at{' '}
                <a href="https://myfamilycalendar.app" className="text-blue hover:underline">
                  myfamilycalendar.app
                </a>
                .
              </li>
              <li>
                For self-hosted instances, please contact the administrator of your instance
                for information about how your data is handled.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Changes to This Policy</h2>
            <p className="text-text-secondary">
              We may update this privacy policy from time to time. We will notify you of any
              significant changes by posting the new policy on this page and updating the
              "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">Contact Us</h2>
            <p className="text-text-secondary">
              If you have any questions about this privacy policy or our data practices,
              please contact us at{' '}
              <a
                href="mailto:contact@myfamilycalendar.app"
                className="text-blue hover:underline"
              >
                contact@myfamilycalendar.app
              </a>
              .
            </p>
          </section>
        </div>

        {/* Back link */}
        <div className="text-center mt-8">
          <Link to="/login" className="text-blue hover:underline">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
